import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Divider, styled, Box, Button, useTheme, List, ListItem, ListItemButton, IconButton } from '@mui/material'
import { Delete } from '@mui/icons-material';
import ImageComponent from 'ui/components/shared/ImageComponent'
import { ParagraphInterface, SkillboxCourseInterface } from 'entities/interfaces'
import SkillboxRowMobile from './SkillboxRowMobile'
import ConfirmationModal from 'ui/components/shared/ConfirmationModal'
import { useNavigate } from "react-router-dom";

const ListElement = styled(ListItem)<{ more?: string; swiped?: boolean }>(({ theme, more, swiped }) => ({
    backgroundColor: more ? theme.palette.grey[800] : theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    height: '7.25rem',
    padding: '0 0.75rem 0 0',
    gap: '0.5rem',
    zIndex: 2,
    transform: swiped ? 'translateX(-80px)' : 'translateX(0)',
    transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

const ProfileImageContainer = styled(Box)({
  width: '3.75rem',
  height: '3.75rem',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: '#e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(161, 183, 165, 0.15)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: 'rgba(161, 183, 165, 0.3)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
  },
  
  '@media (max-width: 390px)': {
    width: '3.5rem',
    height: '3.5rem'
  },
  
  '@media (max-width: 375px)': {
    width: '3.25rem',
    height: '3.25rem'
  },
  
  '@media (max-width: 320px)': {
    width: '3rem',
    height: '3rem',
    border: '1.5px solid rgba(161, 183, 165, 0.15)'
  },
  
  '@media (max-width: 280px)': {
    width: '2.75rem',
    height: '2.75rem',
    border: '1px solid rgba(161, 183, 165, 0.15)'
  }
});

const ListButton = styled(ListItemButton)(() => ({
    height: '100%',
    padding: '0.25rem 0.25rem 0.25rem 1.5rem',
    borderRadius: '0 4px 4px 0'
}))

const ListElementBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    position: 'relative'
}))

// Delete Button hinter dem Content
const DeleteButton = styled(Box)({
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '80px',
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)',
  zIndex: 1
});

const SkillboxListItem = ({
  skill,
  addUserCourse,
  deleteUserCourse,
  setChapterNote,
  getChapterNote,
  getChapterHistory,
  getChapterMaterial,
}: {
  skill: SkillboxCourseInterface,
  addUserCourse: (courseId: number) => Promise<number>,
  deleteUserCourse: (courseId: number) => Promise<number>
  setChapterNote: (chapterId: number, note: string) => Promise<void>;
  getChapterNote: (chapterId: number) => Promise<string>
  getChapterHistory: (chapterId: number) => Promise<ParagraphInterface[]>
  getChapterMaterial: (chapterId: number) => void
}) => {

  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handlers = useSwipeable({
    onSwipedLeft: () => setShowDelete(true),
    onSwipedRight: () => setShowDelete(false),
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  const handleDelete = () => {
    setDeleteConfirmationModal(true);
    setShowDelete(false);
  };

  const confirmDelete = () => {
    deleteUserCourse(skill.id);
    setDeleteConfirmationModal(false);
  };

  return (
    <div {...handlers} style={{ position: "relative" }}>
      <ListElementBox>
        {/* Delete Button (hinter dem Content) */}
        <DeleteButton>
          <IconButton 
            onClick={handleDelete}
            sx={{ color: 'white' }}
          >
            <Delete fontSize="large" />
          </IconButton>
        </DeleteButton>

        {/* Main Content */}
        <ListElement 
          swiped={showDelete}
          onClick={() => {
            if (!showDelete) {
              navigate(`/skillbox/${skill.id}`, { state: { courseTitle: skill.title } })
            }
          }}
        >
          <ListButton>
            <SkillboxRowMobile
              skill={skill}
              addUserCourse={addUserCourse}
              deleteUserCourse={deleteUserCourse}
              setChapterNote={setChapterNote}
              getChapterNote={getChapterNote}
              getChapterHistory={getChapterHistory}
              getChapterMaterial={getChapterMaterial}
            />
          </ListButton>
        </ListElement>
      </ListElementBox>
      
      <Divider sx={{ backgroundColor: theme.palette.grey[900] }} />
      
      {deleteConfirmationModal && (
        <ConfirmationModal
          closeModal={() => setDeleteConfirmationModal(false)}
          confirmColor="error"
          handleConfirm={confirmDelete}
          title="Kurs löschen"
          description={`Sind Sie sicher, dass Sie ${skill.title} löschen wollen?`}
        />
      )}
    </div>
  );
};

export default SkillboxListItem;